import shipLoader from "@/components/ui/ship-loader.gif";
import Header from "@/components/ui/Header";
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Ship } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = () => {
  const suggestionCache = useRef({});
  const [hasSearched, setHasSearched] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAddShip, setShowAddShip] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authStep, setAuthStep] = useState('email');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const searchRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [newShip, setNewShip] = useState({ name: '', imo_number: '', ship_type: 'Bulk' });
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

    useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 120);

    return () => clearTimeout(timer);
  }, [searchQuery]);

    useEffect(() => {

    if (debouncedQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    fetchSuggestions(debouncedQuery);

  }, [debouncedQuery]);

    useEffect(() => {

    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSuggestions([]);
        setActiveIndex(-1);
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
    };

  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setHasSearched(true);
    setSuggestions([]);
    setActiveIndex(-1);
    setIsSearching(true);
    
    try {
      const res = await axios.get(`${API}/ships/search?q=${searchQuery}`);
      setSearchResults(res.data);
    } catch (error) {
      toast.error('Search failed');
    }

    setIsSearching(false);
  };

  const handleKeyDown = (e) => {

    if (suggestions.length > 0) {

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % suggestions.length);
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) =>
          prev <= 0 ? suggestions.length - 1 : prev - 1
        );
      }

      if (e.key === "Enter" && activeIndex >= 0) {
        e.preventDefault();
        navigate(`/ship/${suggestions[activeIndex].id}`);
        setSuggestions([]);
        setActiveIndex(-1);
      }

    }

  };

  const fetchSuggestions = async (query) => {

    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    if (suggestionCache.current[query]) {
      setSuggestions(suggestionCache.current[query]);
      return;
    }

    const previousQuery = Object.keys(suggestionCache.current)
      .sort((a, b) => b.length - a.length)
      .find(q => query.startsWith(q));

    if (previousQuery) {
      const filtered = suggestionCache.current[previousQuery].filter(ship =>
        ship.name.toLowerCase().includes(query.toLowerCase())
      );

      if (filtered.length > 0) {
        setSuggestions(filtered);
        return;
      }
    }

    try {

      const res = await axios.get(`${API}/ships/search?q=${query}`);
      const seenImo = new Set();

      const ships = res.data.filter(ship => {

        if (!ship.imo_number) return true;

        if (seenImo.has(ship.imo_number)) return false;

        seenImo.add(ship.imo_number);
        return true;

      }).slice(0,5);

      suggestionCache.current[query] = ships;

      setSuggestions(ships);

    } catch {
      setSuggestions([]);
    }

  };

  const handleCreateShip = async () => {
    if (!newShip.name.trim()) {
      toast.error('Ship name is required');
      return;
    }

    try {
      const res = await axios.post(`${API}/ships`, newShip, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Ship created successfully');
      setShowAddShip(false);
      navigate(`/ship/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ship');
    }
  };

  const handleRequestOTP = async () => {
    try {
      const res = await axios.post(`${API}/auth/request-otp`, { email });
      setDevOtp(res.data.dev_code);
      setAuthStep('otp');
      toast.success('OTP sent to your email');
    } catch (error) {
      toast.error('Failed to send OTP');
    }
  };

  const handleVerifyOTP = async () => {
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, { email, code: otp });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success('Signed in successfully');
      setShowAuth(false);
      setAuthStep('email');
      window.location.reload();
    } catch (error) {
      toast.error('Invalid OTP');
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
  };

  return (
    <div className="min-h-screen">
    
      <Header
        token={token}
        onSignOut={handleSignOut}
        onShowAuth={() => setShowAuth(true)}
      />

      <div className="max-w-md mx-auto w-full p-4 pt-12">
        <div className="text-center mb-12">
          <Ship className="w-16 h-16 mx-auto mb-4 text-primary" data-testid="hero-ship-icon" />
          <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-2" data-testid="hero-title">
            MarineKnow
          </h1>
          <p className="text-lg text-muted-foreground mb-1" data-testid="hero-tagline">
            By sailors, for sailors
          </p>
          <p className="text-sm text-muted-foreground" data-testid="hero-description">
            Real Ship insights from real crew. Know your next vessel before you join.
          </p>
        </div>

      <form onSubmit={handleSearch} className="mb-10">

        <div className="relative" ref={searchRef}>

          <Input
            type="text"
            placeholder="Search by ship name or IMO number..."
            value={searchQuery}
            onKeyDown={handleKeyDown}
          onChange={(e) => {

            const value = e.target.value;

            setSearchQuery(value);
            setSearchResults([]);
            setSuggestions([]);
            setActiveIndex(-1);
            setHasSearched(false);

            if (value.length === 0) {
              setSuggestions([]);
            }

            if (suggestionCache.current[value]) {

              setSuggestions(suggestionCache.current[value]);

            } else if (suggestions.length > 0) {

              const filtered = suggestions.filter(ship =>
                ship.name.toLowerCase().includes(value.toLowerCase())
              );

              setSuggestions(filtered);

            }

          }}
            className="h-14 bg-card border-border text-lg w-full pl-4 pr-12 rounded-xl focus:ring-2 focus:ring-primary/50"
          />

          {isSearching ? (
            <img
              src={shipLoader}
              alt="Searching..."
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6"
            />
          ) : (
            <button
              type="submit"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
            >
              <Search className="w-5 h-5 text-muted-foreground hover:text-primary cursor-pointer" />
            </button>
          )}

        </div>

        {suggestions.length > 0 && (
          <div className="w-full bg-card border border-border rounded-xl shadow-lg mt-2 max-h-60 overflow-y-auto scroll-smooth">

            {suggestions.map((ship, index) => (
              <div
                key={ship.id}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSearchQuery(ship.name);
                  setSuggestions([]);
                  setActiveIndex(-1);
                  navigate(`/ship/${ship.id}`);
                }}
                className={`px-4 py-3 cursor-pointer flex items-center justify-between border-b border-white/5 last:border-none ${index === activeIndex ? "bg-primary/10" : "hover:bg-primary/10"}`}
              >
                <div className="flex items-start gap-2">
                  <Ship className="w-4 h-4 text-primary mt-1" />

                  <div className="flex flex-col">
                    <span className="font-medium">{ship.name}</span>

                    {ship.imo_number && (
                      <span className="text-xs text-muted-foreground">
                        IMO {ship.imo_number}
                      </span>
                    )}
                  </div>
                </div>

                <span className="text-xs text-muted-foreground">
                  {ship.ship_type}
                </span>
              </div>
            ))}

          </div>
        )}

      </form>

           <div className="grid grid-cols-3 text-center text-xs text-muted-foreground mb-14 gap-4">

            <div>
              <p className="font-semibold text-xl text-primary">120+</p>
              <p>Ships Indexed</p>
            </div>

            <div>
              <p className="font-semibold text-lg text-primary">450+</p>
              <p>Crew Reports</p>
            </div>

            <div>
              <p className="font-semibold text-lg text-primary">100%</p>
              <p>Anonymous</p>
            </div>

          </div>

        {searchResults.length > 0 && suggestions.length === 0 && (
          <div className="mb-6 space-y-3" data-testid="search-results">
            {searchResults.map((ship) => (
              <div
                key={ship.id}
                className="bg-card border border-white/5 rounded-xl p-5 hover:border-primary/30 cursor-pointer transition-colors"
                onClick={() => navigate(`/ship/${ship.id}`)}
                data-testid={`ship-result-${ship.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold" data-testid={`ship-name-${ship.id}`}>{ship.name}</h3>
                    <p className="text-sm text-muted-foreground" data-testid={`ship-type-${ship.id}`}>{ship.ship_type}</p>
                  </div>
                  <Ship className="w-5 h-5 text-primary" />
                </div>
              </div>
            ))}
          </div>
        )}

        {hasSearched && searchResults.length === 0 && searchQuery.trim() !== "" && suggestions.length === 0 && !isSearching && (
          <div className="bg-card border border-border rounded-xl p-5 text-center mb-6">

            <p className="text-sm text-muted-foreground mb-3">
              "{searchQuery}" is not registered in MarineKnow.
            </p>

            <Button
              onClick={() => {
                setNewShip({
                  name: searchQuery,
                  imo_number: "",
                  ship_type: "Bulk"
                });
                setShowAddShip(true);
              }}
              className="rounded-full"
            >
              Add "{searchQuery}" to MarineKnow
            </Button>

          </div>
        )}

        <div className="flex flex-col gap-3">
          <Button
            className="w-full h-12 rounded-full font-semibold shadow-lg shadow-blue-900/20"
            onClick={() => setShowAddShip(true)}
            data-testid="add-ship-button"
          >
            Add Ship Info
          </Button>
          
          {!token && (
            <p className="text-center text-sm text-muted-foreground" data-testid="guest-mode-note">
              Continue as guest or <button onClick={() => setShowAuth(true)} className="text-primary underline" data-testid="signin-link">sign in</button> for verified contributions
            </p>
          )}
        </div>
      </div>

      <Dialog open={showAddShip} onOpenChange={setShowAddShip}>
        <DialogContent className="bg-card border-border" data-testid="add-ship-dialog">
          <DialogHeader>
            <DialogTitle data-testid="add-ship-title">Add New Ship</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="ship-name" data-testid="ship-name-label">Ship Name</Label>
              <Input
                id="ship-name"
                value={newShip.name}
                onChange={(e) => setNewShip({ ...newShip, name: e.target.value })}
                placeholder="MV Atlantic Star"
                className="mt-1"
                data-testid="ship-name-input"
              />
            </div>
            <div>
              <Label htmlFor="imo-number" data-testid="imo-number-label">IMO Number (Optional)</Label>
              <Input
                id="imo-number"
                value={newShip.imo_number}
                onChange={(e) => setNewShip({ ...newShip, imo_number: e.target.value })}
                placeholder="IMO9234567"
                className="mt-1"
                data-testid="imo-number-input"
              />
            </div>
            <div>
              <Label htmlFor="ship-type" data-testid="ship-type-label">Ship Type</Label>
              <Select
                value={newShip.ship_type}
                onValueChange={(value) => setNewShip({ ...newShip, ship_type: value })}
              >
                <SelectTrigger className="mt-1" data-testid="ship-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Bulk" data-testid="ship-type-bulk">Bulk</SelectItem>
                  <SelectItem value="Tanker" data-testid="ship-type-tanker">Tanker</SelectItem>
                  <SelectItem value="Container" data-testid="ship-type-container">Container</SelectItem>
                  <SelectItem value="LNG" data-testid="ship-type-lng">LNG</SelectItem>
                  <SelectItem value="LPG" data-testid="ship-type-lpg">LPG</SelectItem>
                  <SelectItem value="Offshore" data-testid="ship-type-offshore">Offshore</SelectItem>
                  <SelectItem value="Other" data-testid="ship-type-other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCreateShip} className="w-full" data-testid="create-ship-button">
              Create Ship
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="bg-card border-border" data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle data-testid="auth-title">Sign In</DialogTitle>
          </DialogHeader>
          {authStep === 'email' ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="email" data-testid="email-label">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="mt-1"
                  data-testid="email-input"
                />
              </div>
              <Button onClick={handleRequestOTP} className="w-full" data-testid="request-otp-button">
                Send OTP
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="otp" data-testid="otp-label">Enter OTP</Label>
                <Input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="6-digit code"
                  className="mt-1"
                  data-testid="otp-input"
                />
                {devOtp && (
                  <p className="text-xs text-muted-foreground mt-2" data-testid="dev-otp">
                    Dev OTP: {devOtp}
                  </p>
                )}
              </div>
              <Button onClick={handleVerifyOTP} className="w-full" data-testid="verify-otp-button">
                Verify & Sign In
              </Button>
              <Button variant="ghost" onClick={() => setAuthStep('email')} className="w-full" data-testid="back-button">
                Back
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Landing;
